'use client'

import { useEffect, useState, type CSSProperties } from 'react'
import { captureException } from '@/lib/observability/sentry-lazy'
import posthog from '@/lib/analytics/posthog-lazy'
import { AlumniaLogo } from '@/components/alumnia-logo'
import { ENCUENTRO_SCOPE_VALUE } from '@/lib/design/encuentro-scope'
import esPage from '@/messages/encuentro/es/page.json'
import enPage from '@/messages/encuentro/en/page.json'
import frPage from '@/messages/encuentro/fr/page.json'

/**
 * Global Error Boundary — catches errors in the root layout itself.
 * Must include its own <html> and <body> tags because the root layout
 * is unavailable when this component renders.
 *
 * Encuentro (ALU-115): with no root layout there is no next-intl, no token
 * CSS we can count on and no server read of the UI version. So this page is
 * self-contained: it reads what the root layout left on `<html>` (`data-ui`
 * and `lang`) once, on its first client render, and draws the Encuentro page
 * with inline styles and the Encuentro copy of that language. On the server,
 * or when that read fails, it is the classic page, as before.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const [shown] = useState(readShownPage)

  useEffect(() => {
    captureException(error)
    posthog.captureException(error)
  }, [error])

  if (shown.ui === 'encuentro') {
    return <EncuentroGlobalError error={error} reset={reset} locale={shown.locale} />
  }

  return (
    <html lang="en">
      <body className="flex min-h-screen items-center justify-center bg-gray-50 px-4 font-sans antialiased">
        <div className="mx-auto w-full max-w-md text-center">
          {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- global-error renders outside root layout; <Link> may not work */}
          <a
            href="/"
            className="mb-10 inline-block rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
          >
            <AlumniaLogo size={40} />
          </a>

          <div className="mb-8 inline-flex h-16 w-16 items-center justify-center rounded-full bg-red-100 shadow-sm ring-1 ring-red-200/60">
            <svg
              className="h-8 w-8 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
              />
            </svg>
          </div>

          <h1 className="mb-2 text-2xl font-semibold text-gray-900">Something went wrong</h1>
          <p className="mb-6 text-sm text-gray-600">
            An unexpected error occurred. Our team has been notified and is working on a fix.
          </p>

          {error.digest && <p className="mb-4 text-xs text-gray-500">Error ID: {error.digest}</p>}

          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={reset}
              className="inline-flex h-10 items-center justify-center rounded-md bg-blue-900 px-6 text-sm font-medium text-white transition-colors hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Try again
            </button>
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- global-error renders outside root layout; <Link> may not work */}
            <a
              href="/"
              className="inline-flex h-10 items-center justify-center rounded-md border border-gray-300 bg-white px-6 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Go home
            </a>
          </div>
        </div>
      </body>
    </html>
  )
}

type PageCopy = typeof esPage
const COPY: Record<'es' | 'en' | 'fr', PageCopy> = { es: esPage, en: enPage, fr: frPage }
type ShownPage = { ui: 'classic' | 'encuentro'; locale: keyof typeof COPY }

/**
 * What the root layout had put on `<html>` before this boundary replaced it.
 * Server render (no document) or any failure: the Encuentro page. The SSR of
 * this file only happens when the root layout failed, and then `<html>` never
 * carried `data-ui`, so the first client render agrees with the server.
 */
function readShownPage(): ShownPage {
  try {
    if (typeof document === 'undefined') return { ui: 'encuentro', locale: 'en' }
    const html = document.documentElement
    const lang = (html.getAttribute('lang') ?? '').slice(0, 2)
    return {
      // Only a shell the layout stamped as classic shows the classic page; a
      // document without the stamp (the layout itself failed) is Encuentro (ALU-624).
      ui: html.getAttribute('data-ui-shell') === 'classic' ? 'classic' : 'encuentro',
      locale: lang === 'es' || lang === 'fr' ? lang : 'en',
    }
  } catch {
    return { ui: 'encuentro', locale: 'en' }
  }
}

/*
 * Alumnia UI 1.2.1 values (design/alumnia-ui/tokens.json, light), written out
 * because the token CSS may not be on the page when the root layout failed.
 */
const EN = {
  canvas: '#f3f5f8',
  surface: '#ffffff',
  ink: '#172139',
  inkMuted: '#566174',
  lineControl: '#7d8594',
  focus: '#2b50e6',
  font: 'var(--font-geist-sans), Geist, Arial, sans-serif',
}

const buttonBase: CSSProperties = {
  display: 'inline-flex',
  minHeight: '2.75rem',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
  borderRadius: 10,
  padding: '0.625rem 1.25rem',
  font: `600 1rem/1.5 ${EN.font}`,
  textDecoration: 'none',
  cursor: 'pointer',
}

function EncuentroGlobalError({
  error,
  reset,
  locale,
}: {
  error: Error & { digest?: string }
  reset: () => void
  locale: ShownPage['locale']
}) {
  const t = COPY[locale]
  return (
    <html lang={locale} data-ui={ENCUENTRO_SCOPE_VALUE}>
      <body
        data-slot="global-error"
        style={{
          margin: 0,
          minHeight: '100dvh',
          display: 'grid',
          placeItems: 'center',
          padding: '1rem',
          background: EN.canvas,
          color: EN.ink,
          fontFamily: EN.font,
          WebkitFontSmoothing: 'antialiased',
        }}
      >
        <style>{`[data-slot=global-error] a:focus-visible,[data-slot=global-error] button:focus-visible{outline:2px solid ${EN.focus};outline-offset:2px}`}</style>
        <main
          style={{
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: '32rem',
            display: 'grid',
            justifyItems: 'center',
            gap: '1rem',
            textAlign: 'center',
            padding: '2.5rem 1.5rem',
            borderRadius: 16,
            background: EN.surface,
            boxShadow:
              '0 0 0 1px rgba(23,33,57,0.06), 0 1px 2px rgba(23,33,57,0.05), 0 4px 16px rgba(23,33,57,0.05)',
          }}
        >
          {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- global-error renders outside root layout; <Link> may not work */}
          <a href="/" aria-label={t.appHome} style={{ borderRadius: 6 }}>
            {/* eslint-disable-next-line @next/next/no-img-element -- no next/image loader outside the root layout */}
            <img src="/brand/encuentro/lockup-horizontal-blue.svg" alt="" width={124} height={32} />
          </a>
          <h1 style={{ margin: 0, font: `600 1.625rem/1.25 ${EN.font}`, textWrap: 'balance' }}>
            {t.appError.title}
          </h1>
          <p
            style={{
              margin: 0,
              maxWidth: '42ch',
              font: `400 1rem/1.5 ${EN.font}`,
              color: EN.inkMuted,
            }}
          >
            {t.appError.body}
          </p>
          {error.digest && (
            <p
              style={{
                margin: 0,
                fontSize: '0.875rem',
                color: EN.inkMuted,
                overflowWrap: 'anywhere',
              }}
            >
              {t.error.reference.replace('{id}', error.digest)}
            </p>
          )}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: '0.5rem',
              marginTop: '0.5rem',
            }}
          >
            <button
              type="button"
              onClick={reset}
              style={{ ...buttonBase, border: 0, background: EN.ink, color: EN.surface }}
            >
              {t.appError.retry}
            </button>
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- global-error renders outside root layout; <Link> may not work */}
            <a
              href="/"
              style={{
                ...buttonBase,
                border: `1px solid ${EN.lineControl}`,
                background: EN.surface,
                color: EN.ink,
              }}
            >
              {t.appError.home}
            </a>
          </div>
        </main>
      </body>
    </html>
  )
}
